import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Wifi, Tv, Wind, Coffee, Users, BedDouble } from 'lucide-react';
import './Stay.css';

export const Stay = () => {
  const [activeTab, setActiveTab] = useState('premium');

  const roomAmenities = [
    { icon: <Wind size={20} />, label: 'Air Conditioning' },
    { icon: <Wifi size={20} />, label: 'Wi-Fi Access' },
    { icon: <Tv size={20} />, label: 'LED Television' },
    { icon: <Coffee size={20} />, label: 'Room Service' },
    { icon: <BedDouble size={20} />, label: 'Premium Bedding' },
    { icon: <Users size={20} />, label: 'Spacious Interiors' }
  ];

  const premiumFeatures = [
    'Spacious rooms with modern furnishings',
    'Attached bathrooms with premium fixtures',
    'Large windows with serene garden views',
    'Work desk and comfortable seating',
    'Premium linen and toiletries',
    'Individual climate control'
  ];

  const deluxeFeatures = [
    'Enhanced luxury with elegant interiors',
    'Larger room size with separate seating area',
    'Premium bathroom with luxury amenities',
    'Balcony with scenic venue views',
    'Mini-bar and refreshment station',
    'Enhanced lighting and décor',
    'Priority housekeeping service'
  ];

  return (
    <div className="stay-page">
      <section className="stay-hero">
        <div className="container">
          <span className="section-subtitle">Accommodation</span>
          <h1 className="hero-medium">Luxury Guest Rooms</h1>
          <p className="body-large mt-24">
            12 fully air-conditioned rooms designed for elegance and relaxation, perfect for the couple, families, and VIP guests
          </p>
        </div>
      </section>

      {/* Room Tabs */}
      <section className="section-padding">
        <div className="container">
          <div className="room-tabs">
            <button 
              className={`tab-button ${activeTab === 'premium' ? 'active' : ''}`}
              onClick={() => setActiveTab('premium')}
            >
              Premium Rooms (8)
            </button>
            <button 
              className={`tab-button ${activeTab === 'deluxe' ? 'active' : ''}`}
              onClick={() => setActiveTab('deluxe')}
            >
              Deluxe Rooms (4)
            </button>
          </div>

          <div className="tab-content">
            {activeTab === 'premium' && (
              <div className="room-details">
                <div className="room-image-gallery">
                  <div className="main-room-image">
                    <img 
                      src="https://images.unsplash.com/photo-1666626349363-9117bbf2a251?w=800&q=80" 
                      alt="Premium Room"
                      className="room-image"
                    />
                  </div>
                </div>
                <div className="room-info">
                  <h2 className="heading-1 mb-24">Premium Rooms</h2>
                  <p className="body-large mb-32">
                    Our 8 Premium Rooms offer spacious interiors with modern furnishings, creating a comfortable retreat for your guests. 
                    Each room features attached bathrooms, serene views, and thoughtful amenities for a relaxing stay.
                  </p>
                  
                  <h3 className="heading-3 mb-16">Room Features</h3>
                  <ul className="feature-list mb-32">
                    {premiumFeatures.map((feature, idx) => (
                      <li key={idx}>{feature}</li>
                    ))}
                  </ul>

                  <Link to="/contact" className="btn-primary">
                    Inquire About Availability
                  </Link>
                </div>
              </div>
            )}

            {activeTab === 'deluxe' && (
              <div className="room-details">
                <div className="room-image-gallery">
                  <div className="main-room-image">
                    <img 
                      src="https://images.unsplash.com/photo-1759519238029-689e99c6d19e?w=800&q=80" 
                      alt="Deluxe Room"
                      className="room-image"
                    />
                  </div>
                </div>
                <div className="room-info">
                  <h2 className="heading-1 mb-24">Deluxe Rooms</h2>
                  <p className="body-large mb-32">
                    Experience enhanced luxury in our 4 Deluxe Rooms, featuring larger spaces, elegant interiors, and premium amenities. 
                    These rooms are perfect for the couple and VIP guests seeking an elevated stay experience.
                  </p>
                  
                  <h3 className="heading-3 mb-16">Room Features</h3>
                  <ul className="feature-list mb-32">
                    {deluxeFeatures.map((feature, idx) => (
                      <li key={idx}>{feature}</li>
                    ))}
                  </ul>

                  <Link to="/contact" className="btn-primary">
                    Inquire About Availability
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Amenities Section */}
      <section className="section-padding amenities-section">
        <div className="container">
          <div className="section-header">
            <span className="section-subtitle">Comfort</span>
            <h2 className="heading-1">Room Amenities</h2>
          </div>
          <div className="amenities-grid">
            {roomAmenities.map((amenity, index) => (
              <div key={index} className="amenity-card">
                <div className="amenity-icon">{amenity.icon}</div>
                <p className="body-regular">{amenity.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="section-padding services-section">
        <div className="container">
          <div className="two-column-layout">
            <div className="image-column">
              <img 
                src="https://customer-assets.emergentagent.com/job_luxweddings/artifacts/9dp1zzek_IMG-20251012-WA0011.jpg" 
                alt="The Green Valley Entrance"
                className="feature-image"
              />
            </div>
            <div className="content-column">
              <span className="section-subtitle">Services</span>
              <h2 className="heading-1 mb-24">Hospitality Beyond Rooms</h2>
              <p className="body-large mb-32">
                Your stay at The Green Valley includes access to all venue amenities and personalized services to ensure a memorable experience.
              </p>
              <ul className="feature-list">
                <li>24/7 room service and housekeeping</li>
                <li>Complimentary breakfast during events</li>
                <li>Access to all venue spaces</li>
                <li>Valet parking for room guests</li>
                <li>Concierge assistance</li>
                <li>Luggage handling and storage</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <div className="glass-card cta-card">
            <h2 className="hero-medium text-center">Book Your Stay</h2>
            <p className="body-large text-center mt-24 mb-32">
              Rooms are available as part of our event packages. Contact us for availability and special requests.
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn-primary">Check Availability</Link>
              <a href="https://wa.me/918826372788?text=Hi!%20I'd%20like%20to%20inquire%20about%20room%20availability." 
                 target="_blank" 
                 rel="noopener noreferrer" 
                 className="btn-secondary">
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Stay;
